import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { OrganIcon, ORGAN_COLORS, OrganImage } from "@/components/common/OrganIcon";

const TYPE_LABELS = {
  recipient: "Recipient", living_donor: "Living donor", family_member: "Family member",
  caregiver: "Caregiver", evaluation_patient: "Evaluation", other: "Contributor",
};
const ORGAN_LABELS = { kidney: "Kidney", liver: "Liver", heart: "Heart", lung: "Lung", pancreas: "Pancreas", intestine: "Intestine" };

export function OrganCard({ organ }) {
  const navigate = useNavigate();
  const c = ORGAN_COLORS[organ.slug] || ORGAN_COLORS.other;
  return (
    <button
      data-testid={`organ-card-${organ.slug}`}
      onClick={() => navigate(`/organ/${organ.slug}`)}
      style={{ backgroundColor: c.bg, "--oc-border": c.border }}
      className="organ-card group p-5 flex flex-col items-start justify-between text-left h-44 overflow-hidden"
    >
      <span className="transition-transform duration-200 group-hover:scale-105">
        <OrganImage slug={organ.slug} className="w-16 h-16" rounded="rounded-xl" />
      </span>
      <div className="w-full">
        <div className="text-lg font-medium text-ink">{organ.name}</div>
        <div className="text-[11px] font-mono text-neutral-500 mt-1.5 leading-relaxed">
          <span className="block whitespace-nowrap">{organ.centre_count} {organ.centre_count === 1 ? "center" : "centers"}</span>
          <span className="block whitespace-nowrap">{organ.journey_count} {organ.journey_count === 1 ? "journey" : "journeys"}</span>
        </div>
      </div>
    </button>
  );
}

const STATE_TINTS = [
  { bg: "#EAF3F0", fg: "#3E7A66" },
  { bg: "#F5EDE1", fg: "#A0693A" },
  { bg: "#F9EAEB", fg: "#B05A5A" },
  { bg: "#E9EFF7", fg: "#55799E" },
  { bg: "#F4F0E1", fg: "#8F7A3E" },
  { bg: "#F2EBF4", fg: "#7A628E" },
];

export function StateCard({ state }) {
  const navigate = useNavigate();
  const t = STATE_TINTS[(state.code.charCodeAt(0) + state.code.charCodeAt(1)) % STATE_TINTS.length];
  const c = state.centre_count || 0;
  const j = state.journey_count || 0;
  return (
    <button
      data-testid={`state-card-${state.code}`}
      onClick={() => navigate(`/state/${state.code}`)}
      style={{ backgroundColor: t.bg, "--oc-border": "rgba(17,17,17,0.06)" }}
      className="organ-card group w-full p-5 flex items-center gap-4 text-left h-24"
    >
      <span
        className="w-12 h-12 shrink-0 rounded-xl bg-white/80 shadow-sm overflow-hidden transition-transform duration-200 group-hover:scale-105"
      >
        <img
          src={`/assets/states/${state.code}.jpeg`}
          alt={state.name}
          className="w-full h-full object-cover scale-[1.15]"
          loading="lazy"
          onError={(e) => {
            e.currentTarget.style.display = "none";
            e.currentTarget.parentElement.textContent = state.code;
            e.currentTarget.parentElement.style.color = t.fg;
            e.currentTarget.parentElement.classList.add("flex", "items-center", "justify-center", "font-mono", "font-semibold", "text-sm", "tracking-wider");
          }}
        />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-base font-medium text-ink truncate">{state.name}</div>
        <div className="text-[11px] font-mono text-neutral-500 mt-0.5">
          {c} {c === 1 ? "center" : "centers"} · {j} {j === 1 ? "journey" : "journeys"}
        </div>
      </div>
      <ArrowUpRight className="w-4 h-4 shrink-0 text-neutral-300 group-hover:text-rust transition-colors" />
    </button>
  );
}

export function HospitalCard({ centre }) {
  const navigate = useNavigate();
  const primary = (centre.organs || [])[0];
  const col = ORGAN_COLORS[primary] || ORGAN_COLORS.other;
  return (
    <button
      data-testid={`hospital-card-${centre.hospital_slug}`}
      onClick={() => navigate(`/hospital/${centre.hospital_slug}`)}
      className="group bg-white border border-neutral-200 hover:border-neutral-400 hover:-translate-y-0.5 hover:shadow-[0_12px_30px_-14px_rgba(17,17,17,0.25)] transition-all duration-200 p-6 flex flex-col justify-between text-left h-full"
    >
      <div>
        <div className="flex items-start gap-4">
          <span className="w-11 h-11 shrink-0 rounded-xl flex items-center justify-center font-mono text-base font-medium overflow-hidden" style={{ backgroundColor: col.bg, color: col.fg }}>
            {centre.hospital_logo_url
              ? <img src={centre.hospital_logo_url} alt="" className="w-full h-full object-contain p-1.5" loading="lazy" />
              : centre.hospital_name.charAt(0)}
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-medium text-ink group-hover:text-rust transition-colors leading-snug">
              {centre.hospital_name}
            </h3>
            <div className="text-[11px] font-mono text-neutral-500 mt-1">
              {centre.city && centre.city.toLowerCase() !== (centre.state_name || "").toLowerCase() ? `${centre.city}, ${centre.state_name}` : centre.state_name}
            </div>
          </div>
          <ArrowUpRight className="w-4 h-4 mt-1 shrink-0 text-neutral-300 group-hover:text-rust transition-colors" />
        </div>
        <div className="flex flex-wrap gap-1.5 mt-4">
          {(centre.organs || []).map((o) => {
            const oc = ORGAN_COLORS[o] || ORGAN_COLORS.other;
            return (
              <span key={o} style={{ backgroundColor: oc.bg, color: oc.fg }}
                className="text-[11px] font-mono px-2 py-0.5 rounded-full">
                {ORGAN_LABELS[o] || o}
              </span>
            );
          })}
        </div>
      </div>
      <div className="mt-5 pt-4 border-t border-neutral-100 flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-mono text-neutral-600 bg-neutral-100 px-2.5 py-1 rounded border border-neutral-200">
          <span className="w-1.5 h-1.5 rounded-full bg-neutral-400" />
          {centre.notto_status}
        </span>
        <span className="flex items-baseline gap-2">
          <span className="text-2xl font-light font-mono text-ink leading-none tabular-nums">{centre.patient_journey_count || 0}</span>
          <span className="text-[10px] font-mono uppercase tracking-widest text-neutral-400">journeys</span>
        </span>
      </div>
    </button>
  );
}

export function JourneyCard({ journey }) {
  const navigate = useNavigate();
  const c = ORGAN_COLORS[journey.organ] || ORGAN_COLORS.other;
  const snippet = journey.story_text || journey.transcript || "A voice journey. Listen to hear this story.";
  return (
    <button
      data-testid={`journey-card-${journey.id}`}
      onClick={() => navigate(`/story/${journey.id}`)}
      style={{ backgroundColor: c.bg, "--oc-border": c.border }}
      className="organ-card group p-6 flex flex-col justify-between text-left h-full"
    >
      <div>
        <div className="flex items-center gap-2 mb-3" style={{ color: c.fg }}>
          <OrganIcon slug={journey.organ} className="w-5 h-5" />
          <span className="text-[11px] font-mono uppercase tracking-wider font-medium">
            {ORGAN_LABELS[journey.organ] || journey.organ} · {TYPE_LABELS[journey.journey_type] || "Contributor"}
            {journey.journey_year ? ` · ${journey.journey_year}` : ""}
          </span>
        </div>
        <p className="text-neutral-800 text-base leading-relaxed italic line-clamp-4">
          "{snippet.slice(0, 180)}{snippet.length > 180 ? "…" : ""}"
        </p>
      </div>
      <div className="mt-5 flex items-center justify-between">
        <span className="text-[11px] font-mono text-neutral-500 truncate min-w-0">{journey.hospital_name || journey.state_name || journey.public_label}</span>
        {journey.has_audio && (
          <span className="inline-flex items-center gap-1.5 text-[11px] font-mono bg-white/70 text-neutral-800 px-2.5 py-1 rounded-full">
            ▶ Voice
          </span>
        )}
      </div>
    </button>
  );
}
